import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ArrowLeft, FilePlus2, LogOut, Mail, Pencil, Phone } from "lucide-react";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchPersonnelSummary, PersonnelSummary } from "services/personnel.service";
import UserAvatar from "components/user-avatar/user-avatar";
import { setModalxComponent, setModalxComponentProps } from "components/modalx/modalx.slice";
import { getJobLeaveStatusLabel } from "utils/jobLeaveStatus";
import PersonnelCardTabs from "./PersonnelCardTabs";
import PersonalCodeField from "./PersonalCodeField";
import useScrollActiveTabIntoView from "./useScrollActiveTabIntoView";
import {
  canStartLeave,
  CardTab,
  contractTypeKey,
  isEmployed,
  isLeaveActive,
  formatDay,
  medicalState,
  primaryOnboarding,
  resolveCardTab,
  visibleCardTabs,
} from "./personnel-helpers";

const chipClass = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";
const actionClass =
  "inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 max-md:min-h-11";

/**
 * «Darbuotojo kortelė» (route /app/:companyId/personnel/:userId): one person's
 * summary and every HR section about them. What the viewer sees is decided by
 * the backend (403 on the summary, `viewerCan` per tab, salary only when
 * sent); the tab lives in `?tab=` so Back and links keep it.
 */
export default function PersonnelCardPage() {
  const { companyId, userId } = useParams<{ companyId: string; userId: string }>();
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "lt") as "lt" | "en";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<PersonnelSummary | null>(null);
  const [error, setError] = useState<"forbidden" | "failed" | null>(null);
  const requestRef = useRef(0);
  const tabStrip = useRef<HTMLElement>(null);

  const load = useCallback(() => {
    if (!companyId || !userId) return;
    const requestId = ++requestRef.current;
    fetchPersonnelSummary(companyId, userId)
      .then((data) => {
        if (requestId !== requestRef.current) return;
        setSummary(data);
        setError(null);
      })
      .catch((e: any) => {
        if (requestId !== requestRef.current) return;
        const status = e?.response?.status;
        setError(status === 403 || status === 404 ? "forbidden" : "failed");
      });
  }, [companyId, userId]);

  useEffect(() => {
    setSummary(null);
    setError(null);
    load();
  }, [load]);

  // The employee edit form is a modalx modal; refresh the card once it closes.
  const modalOpen = useAppSelector((state) => state.modalxReducer.isOpen);
  const editingRef = useRef(false);
  useEffect(() => {
    if (!modalOpen && editingRef.current) {
      editingRef.current = false;
      load();
    }
  }, [modalOpen, load]);

  // The strip appears only once the summary is loaded, so that counts as a change too.
  useScrollActiveTabIntoView(tabStrip, `${searchParams.get("tab")}:${summary ? "loaded" : "loading"}`);

  if (!companyId || !userId) return null;

  const backLink = (
    <Link
      to={`/app/${companyId}/personnel`}
      className="mb-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline max-md:min-h-11"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t("hr_module")}
    </Link>
  );

  if (error || !summary) {
    return (
      <div className="w-full px-6 py-4">
        {backLink}
        {error === "forbidden" ? (
          <p className="text-sm text-gray-600">{t("personnel_card_forbidden")}</p>
        ) : error === "failed" ? (
          <div className="flex items-center gap-3 text-sm text-red-700">
            {t("personnel_card_load_failed")}
            <button type="button" className="underline" onClick={load}>
              {t("retry")}
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t("loading")}</p>
        )}
      </div>
    );
  }

  const tabs = visibleCardTabs(summary);
  const tab = resolveCardTab(searchParams.get("tab"), tabs);
  const selectTab = (next: CardTab) =>
    setSearchParams(next === "summary" ? {} : { tab: next }, { replace: true });

  const { person, employmentTerms: terms, viewerCan } = summary;
  const record = primaryOnboarding(summary.onboardings);
  // Only the code changed — put the stored one into the card, no reload needed.
  const showSavedPersonalCode = (personalCode: string | null) =>
    setSummary((current) => current && { ...current, person: { ...current.person, personalCode } });
  // person.hiringDate also reaches viewers who do not get the terms (WS).
  const hiringDate = formatDay(terms?.hiringDate ?? person.hiringDate ?? record?.hiringDate ?? null);
  const leaveActive = isLeaveActive(summary.jobLeave);
  const employed = isEmployed(summary.membership);
  const medical = medicalState(summary.medical?.validUntil ?? null);

  const openEdit = () => {
    editingRef.current = true;
    dispatch(setModalxComponent("UserForm"));
    dispatch(setModalxComponentProps({ id: userId }));
  };
  const openRecord = () => record && navigate(`/app/${companyId}/onboarding/${record.id}`);

  const metaLine = [
    summary.positionsText,
    summary.departments.map((department) => department.name).join(", "),
    summary.manager?.name ? `${t("manager")}: ${summary.manager.name}` : "",
  ].filter(Boolean);
  const contractLine = [
    hiringDate ? t("personnel_employed_since", { date: hiringDate }) : "",
    terms?.originalContractNumber ? t("personnel_contract_no", { number: terms.originalContractNumber }) : "",
    contractTypeKey(terms?.contractType) ? t(contractTypeKey(terms?.contractType)!) : "",
  ].filter(Boolean);

  return (
    <div className="w-full px-6 py-4">
      {backLink}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:gap-4">
            <UserAvatar
              companyId={companyId}
              user={{ id: person.userId, name: person.name, avatarFileId: person.avatarFileId }}
              size={56}
              rounded="full"
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold text-gray-900">{person.name || "—"}</h1>
                {summary.membership.isDisabled || summary.membership.employmentStatus === "left" ? (
                  <span className={clsx(chipClass, "bg-gray-200 text-gray-700")}>{t("personnel_status_left_one")}</span>
                ) : summary.membership.employmentStatus === "hiring" ? (
                  <span className={clsx(chipClass, "bg-amber-50 text-amber-800")}>{t("personnel_status_hiring")}</span>
                ) : (
                  <span className={clsx(chipClass, "bg-green-50 text-green-800")}>
                    {t("personnel_status_active")}
                  </span>
                )}
                {leaveActive && (
                  <span className={clsx(chipClass, "bg-orange-50 text-orange-800")}>
                    {t("personnel_chip_job_leave", {
                      status: getJobLeaveStatusLabel(summary.jobLeave!.status || undefined, t, lang),
                    })}
                  </span>
                )}
                {(medical === "expired" || medical === "expiring") && (
                  <span
                    className={clsx(
                      chipClass,
                      medical === "expired" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"
                    )}
                  >
                    {t(medical === "expired" ? "personnel_chip_medical_expired" : "personnel_chip_medical_expiring", {
                      date: formatDay(summary.medical?.validUntil ?? null),
                    })}
                  </span>
                )}
              </div>
              {metaLine.length > 0 && <p className="mt-0.5 text-sm text-gray-700">{metaLine.join(" · ")}</p>}
              {contractLine.length > 0 && <p className="mt-0.5 text-sm text-gray-500">{contractLine.join(" · ")}</p>}
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                {person.email && (
                  <a
                    href={`mailto:${person.email}`}
                    className="inline-flex items-center gap-1 break-all hover:text-blue-700 max-md:min-h-11"
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {person.email}
                  </a>
                )}
                {(person.companyPhone || person.phone) && (
                  <span className="inline-flex flex-wrap items-center gap-x-1">
                    <Phone className="h-3.5 w-3.5" aria-hidden />
                    {[person.companyPhone, person.phone].filter(Boolean).map((number, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && ","}
                        <a
                          href={`tel:${number!.replace(/\s+/g, "")}`}
                          className="hover:text-blue-700 max-md:inline-flex max-md:min-h-11 max-md:items-center"
                        >
                          {number}
                        </a>
                      </React.Fragment>
                    ))}
                  </span>
                )}
                {person.personalCode !== undefined && (
                  <PersonalCodeField
                    companyId={companyId}
                    userId={userId}
                    value={person.personalCode}
                    canEdit={viewerCan.editPersonalCode}
                    onSaved={showSavedPersonalCode}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {viewerCan.edit && (
              <button type="button" className={actionClass} onClick={openEdit}>
                <Pencil className="h-4 w-4" aria-hidden />
                {t("edit")}
              </button>
            )}
            {viewerCan.manageOnboarding && record && employed && (
              <button
                type="button"
                className={actionClass}
                // With «Sutartis ir pakeitimai» on the card the amendment is made right here.
                onClick={tabs.includes("contract") ? () => selectTab("contract") : openRecord}
                title={tabs.includes("contract") ? undefined : t("personnel_opens_record_hint")}
              >
                <FilePlus2 className="h-4 w-4" aria-hidden />
                {t("personnel_new_amendment")}
              </button>
            )}
            {canStartLeave(summary) && (
              <button type="button" className={actionClass} onClick={openRecord} title={t("personnel_opens_record_hint")}>
                <LogOut className="h-4 w-4" aria-hidden />
                {t("personnel_start_leave")}
              </button>
            )}
          </div>
        </div>
      </div>

      {tabs.length > 1 && (
        // One scrolling line on a phone (HR gets up to 12 tabs); wrapped rows from md up.
        <nav
          ref={tabStrip}
          className="mb-4 flex gap-2 overflow-x-auto border-b border-gray-200 md:flex-wrap md:overflow-visible"
          aria-label={t("personnel_card_title")}
        >
          {tabs.map((item) => (
            <button
              key={item}
              type="button"
              aria-current={item === tab ? "page" : undefined}
              onClick={() => selectTab(item)}
              className={clsx(
                // md:-mb-px only from md up: inside the scrolling strip the overhang would be clipped.
                "shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium max-md:py-3 md:-mb-px",
                item === tab
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900"
              )}
            >
              {t(`personnel_tab_${item}`)}
            </button>
          ))}
        </nav>
      )}

      <PersonnelCardTabs
        tab={tab}
        companyId={companyId}
        userId={userId}
        summary={summary}
        onSelectTab={selectTab}
        onSummaryChanged={load}
      />
    </div>
  );
}
